import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { TestTubes, Plus, CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { localToday } from '@/lib/dateRange';

const RESULT_COLORS = { passed: 'bg-success/20 text-success', failed: 'bg-error/20 text-error', pending: 'bg-warning/20 text-warning', partial: 'bg-info/20 text-info' };

export default function QualityControlPage() {
  const [checks, setChecks] = useState([]);
  const [orders, setOrders] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [form, setForm] = useState({ production_order_id: '', product_name: '', inspector: '', check_date: localToday(), batch_size: 0, passed: 0, failed: 0, defect_type: '', result: 'pending', notes: '' });
  const [saving, setSaving] = useState(false);

  const fetchData = async () => {
    try {
      const [checksRes, ordersRes] = await Promise.all([
        api.get('/factory/quality-checks'),
        api.get('/factory/production-orders'),
      ]);
      setChecks(checksRes.data);
      setOrders(ordersRes.data);
    } catch { toast.error('Failed'); }
  };
  useEffect(() => { fetchData(); }, []);

  const handleSave = async () => {
    if (!form.product_name) return toast.error('Product name required');
    setSaving(true);
    try {
      const payload = { ...form, batch_size: parseInt(form.batch_size) || 0, passed: parseInt(form.passed) || 0, failed: parseInt(form.failed) || 0 };
      await api.post('/factory/quality-checks', payload);
      toast.success('Quality check recorded');
      setShowDialog(false);
      setForm({ production_order_id: '', product_name: '', inspector: '', check_date: localToday(), batch_size: 0, passed: 0, failed: 0, defect_type: '', result: 'pending', notes: '' });
      fetchData();
    } catch { toast.error('Failed'); }
    finally { setSaving(false); }
  };

  const totalChecked = checks.reduce((s, c) => s + (c.batch_size || 0), 0);
  const totalPassed = checks.reduce((s, c) => s + (c.passed || 0), 0);
  const totalFailed = checks.reduce((s, c) => s + (c.failed || 0), 0);
  const passRate = totalChecked > 0 ? ((totalPassed / totalChecked) * 100).toFixed(1) : 0;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="quality-control-page">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center shadow-lg shadow-emerald-500/20">
              <TestTubes size={22} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground" data-testid="qc-title">Quality Control</h1>
              <p className="text-[13px] text-muted-foreground mt-0.5 font-medium">Inspect production batches & track defects</p>
            </div>
          </div>
          <Dialog open={showDialog} onOpenChange={setShowDialog}>
            <DialogTrigger asChild>
              <Button className="rounded-xl h-9 text-[13px]" data-testid="new-qc-btn"><Plus size={14} className="mr-1.5" />New Check</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg" data-testid="qc-dialog">
              <DialogHeader><DialogTitle className="font-outfit">Record Quality Check</DialogTitle></DialogHeader>
              <div className="space-y-3 mt-2">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-[12px]">Product Name *</Label><Input value={form.product_name} onChange={e => setForm({...form, product_name: e.target.value})} data-testid="qc-product-name" /></div>
                  <div><Label className="text-[12px]">Inspector</Label><Input value={form.inspector} onChange={e => setForm({...form, inspector: e.target.value})} /></div>
                </div>
                {orders.length > 0 && (
                  <div><Label className="text-[12px]">Production Order</Label>
                    <Select value={form.production_order_id} onValueChange={v => { const o = orders.find(o => o.id === v); setForm({...form, production_order_id: v, product_name: o?.product_name || form.product_name}); }}>
                      <SelectTrigger><SelectValue placeholder="Optional" /></SelectTrigger>
                      <SelectContent>{orders.map(o => <SelectItem key={o.id} value={o.id}>{o.order_number} - {o.product_name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
                <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  <div><Label className="text-[12px]">Check Date</Label><Input type="date" value={form.check_date} onChange={e => setForm({...form, check_date: e.target.value})} /></div>
                  <div><Label className="text-[12px]">Batch Size</Label><Input type="number" value={form.batch_size} onChange={e => setForm({...form, batch_size: e.target.value})} /></div>
                  <div><Label className="text-[12px]">Passed</Label><Input type="number" value={form.passed} onChange={e => setForm({...form, passed: e.target.value})} /></div>
                  <div><Label className="text-[12px]">Failed</Label><Input type="number" value={form.failed} onChange={e => setForm({...form, failed: e.target.value})} /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-[12px]">Defect Type</Label><Input value={form.defect_type} onChange={e => setForm({...form, defect_type: e.target.value})} placeholder="e.g. Crack, Discoloration" /></div>
                  <div><Label className="text-[12px]">Result</Label>
                    <Select value={form.result} onValueChange={v => setForm({...form, result: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="passed">Passed</SelectItem>
                        <SelectItem value="failed">Failed</SelectItem>
                        <SelectItem value="partial">Partial</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div><Label className="text-[12px]">Notes</Label><Textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows={2} /></div>
                <Button onClick={handleSave} disabled={saving} className="w-full rounded-xl">{saving ? 'Saving...' : 'Record Check'}</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="stat-card border-0 bg-white"><CardContent className="pt-4 pb-3.5 px-4"><p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-[0.08em]">Total Checks</p><p className="text-2xl font-bold font-outfit text-foreground mt-1">{checks.length}</p></CardContent></Card>
          <Card className="stat-card border-0 bg-white"><CardContent className="pt-4 pb-3.5 px-4"><p className="text-[10px] font-semibold text-success uppercase tracking-[0.08em]">Pass Rate</p><p className="text-2xl font-bold font-outfit text-success mt-1">{passRate}%</p></CardContent></Card>
          <Card className="stat-card border-0 bg-white"><CardContent className="pt-4 pb-3.5 px-4"><p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-[0.08em]">Units Checked</p><p className="text-2xl font-bold font-outfit text-info mt-1">{totalChecked.toLocaleString()}</p></CardContent></Card>
          <Card className="stat-card border-0 bg-white"><CardContent className="pt-4 pb-3.5 px-4"><p className="text-[10px] font-semibold text-error uppercase tracking-[0.08em]">Defects</p><p className="text-2xl font-bold font-outfit text-error mt-1">{totalFailed}</p></CardContent></Card>
        </div>

        {/* QC Table */}
        <Card className="border-0 shadow-[0_0_0_1px_rgba(0,0,0,0.03),0_1px_4px_rgba(0,0,0,0.04)] bg-white rounded-2xl overflow-hidden">
          <CardContent className="px-0 pt-4">
            {checks.length === 0 ? (
              <div className="py-12 text-center text-muted-foreground">
                <TestTubes size={32} className="mx-auto mb-3 opacity-30" />
                <p className="text-[13px]">No quality checks recorded</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="qc-table">
                  <thead><tr>
                    <th className="text-left">Product</th>
                    <th className="text-left">Inspector</th>
                    <th className="text-left">Date</th>
                    <th className="text-right">Batch</th>
                    <th className="text-right">Passed</th>
                    <th className="text-right">Failed</th>
                    <th className="text-center">Result</th>
                    <th className="text-left">Defect</th>
                  </tr></thead>
                  <tbody>
                    {checks.map(c => (
                      <tr key={c.id} data-testid="qc-row">
                        <td className="font-medium text-foreground">{c.product_name}</td>
                        <td className="text-muted-foreground">{c.inspector || '-'}</td>
                        <td className="text-muted-foreground">{c.check_date}</td>
                        <td className="text-right tabular-nums font-medium">{c.batch_size}</td>
                        <td className="text-right text-success font-semibold tabular-nums">{c.passed}</td>
                        <td className="text-right text-error font-semibold tabular-nums">{c.failed}</td>
                        <td className="text-center"><Badge className={`${RESULT_COLORS[c.result] || 'bg-muted'} text-[10px] capitalize`}>{c.result}</Badge></td>
                        <td className="text-muted-foreground">{c.defect_type || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
